import React from 'react';
import { Box, Button, Loader, Tooltip } from '@wix/design-system';
import type { ButtonPriority, ButtonSize } from '@wix/design-system';
import { openAppClick } from '@wix/bi-logger-app-market-data/v2';
import type { AppBadge, ManagedApp } from '@wix/app-market-services';
import s from './open-app-button.scss';
import {
  useTranslation,
  useEssentials,
  useInstaller,
  useJunkYard,
  useRouterContext,
  useServices,
  useSiteContext,
  useUserContext,
  getOpenAppClickBiData,
  QueryDecorator,
} from '@wix/app-market-core';
import { RoutePath } from '@wix/app-market-core-types';
import type { AppPageType } from '@wix/app-market-core-components-types';

export interface IOpenAppButtonProps {
  origin: string;
  managedApp?: ManagedApp;
  isDisabled: boolean;
  tooltipContent: string;
  app: {
    id: string;
    isBuiltIn: boolean;
    appBadges?: AppBadge[];
  };
  size: ButtonSize;
  priority: ButtonPriority;
  openMobileInstallationBlockedModal(): void;
  appPageType: AppPageType;
}

export function OpenAppButton({
  origin,
  managedApp: existingManagedApp,
  isDisabled,
  tooltipContent,
  app,
  size,
  priority,
  openMobileInstallationBlockedModal,
  appPageType,
}: IOpenAppButtonProps) {
  const { t } = useTranslation();
  const { metaSiteId } = useSiteContext();
  const { isMobile } = useUserContext();
  const services = useServices();
  const { biLogger } = useEssentials();
  const { router } = useRouterContext();
  const { routerData, goBackToReferrer, openApp } = useJunkYard();
  const installer = useInstaller();

  return (
    <QueryDecorator
      queryFn={async () => {
        if (!metaSiteId) {
          return Promise.resolve(null);
        }
        if (existingManagedApp?.toJSON()) {
          return Promise.resolve(existingManagedApp);
        }
        const managedApps = await services.getManagedApps({});
        return managedApps.findByAppId(app.id);
      }}
      queryKey={`open-app-button-${app.id}`}
      csrOnly
    >
      {({ data: managedApp, isLoading }) => {
        const isAddAgainMode =
          !!managedApp?.isAddAgainAllowed && !!installer.addAgain;

        return (
          <Tooltip
            className={s.tooltip}
            inline
            disabled={!tooltipContent}
            content={tooltipContent}
          >
            <Box direction="vertical" flexGrow={1}>
              <Button
                disabled={isDisabled || isLoading}
                size={size}
                priority={priority}
                onClick={() => {
                  biLogger.report(
                    openAppClick(
                      getOpenAppClickBiData({
                        appId: app.id,
                        ctaType: isAddAgainMode ? 'add_again' : 'open_app',
                        tagType: 'app_page',
                        tagName: app.id,
                        routerData,
                        referrerNameForBI: goBackToReferrer?.referrerNameForBI,
                        origin,
                        appBadges: app.appBadges,
                        appPageType,
                      }),
                    ),
                  );

                  if (isMobile) {
                    return openMobileInstallationBlockedModal();
                  }

                  if (app.isBuiltIn) {
                    return router.navigateTo({
                      path: RoutePath.WIX_OFFERING,
                      payload: {
                        appId: app.id,
                      },
                    });
                  }

                  if (isAddAgainMode) {
                    return installer.addAgain?.({
                      appId: app.id,
                      appVersion: managedApp.installedVersion,
                    });
                  }

                  const managedAppJson = managedApp?.toJSON();
                  managedAppJson && openApp(managedAppJson);
                }}
              >
                {isLoading ? (
                  <Loader size="tiny" />
                ) : isAddAgainMode ? (
                  t('solution.page.addAgain')
                ) : (
                  t('solution.page.open')
                )}
              </Button>
            </Box>
          </Tooltip>
        );
      }}
    </QueryDecorator>
  );
}
